/**
 * Unified Schedule Types
 *
 * TypeScript types for the unified schedule system that supports both
 * system tasks and workflow schedules via Temporal.
 *
 * 对应后端 api/schemas/schedule.py
 */

import type { RuntimeOptions, UserFilterOptions } from './imageWorkflow';
import type { WorkflowTargetType } from './users';

// ============================================================================
// Schedule Type Discriminator
// ============================================================================

/** 调度类型：系统任务 或 工作流 */
export type UnifiedScheduleType = 'system_task' | 'workflow';

// ============================================================================
// Config Types
// ============================================================================

/** 系统任务调度配置 (e.g., artifact_cleanup, transit_precompute) */
export interface SystemTaskScheduleConfig {
  task_type: string;
  config: Record<string, unknown>;
}

/** 时区窗口配置：按用户本地时间分批执行 */
export interface TimezoneWindow {
  target_hour: number;      // 0-23，用户本地时区的目标小时
  default_timezone: string; // 无时区设置的用户的回退时区
}

/** 工作流调度配置 */
export interface WorkflowScheduleConfig {
  workflow_id: string;
  target_type: WorkflowTargetType;
  target_user_ids?: string[] | null;
  user_filter?: UserFilterOptions | null;
  runtime_options?: RuntimeOptions | null;
  timezone_window?: TimezoneWindow | null;
}

// ============================================================================
// API Request Types
// ============================================================================

/** 创建统一调度请求 */
export interface CreateUnifiedScheduleRequest {
  name: string;
  description?: string | null;
  schedule_type: UnifiedScheduleType;
  cron_expression: string;
  timezone?: string; // 默认 Asia/Shanghai
  is_enabled?: boolean; // 默认 true
  system_task_config?: SystemTaskScheduleConfig | null;
  workflow_config?: WorkflowScheduleConfig | null;
}

/** 更新统一调度请求（所有字段可选） */
export interface UpdateUnifiedScheduleRequest {
  name?: string | null;
  description?: string | null;
  cron_expression?: string | null;
  timezone?: string | null;
  system_task_config?: SystemTaskScheduleConfig | null;
  workflow_config?: WorkflowScheduleConfig | null;
}

// ============================================================================
// API Response Types
// ============================================================================

/** 统一调度响应 */
export interface UnifiedScheduleResponse {
  id: string;
  name: string;
  description?: string | null;
  schedule_type: UnifiedScheduleType;
  cron_expression: string;
  timezone: string;
  is_enabled: boolean;
  temporal_schedule_id: string;
  system_task_config?: SystemTaskScheduleConfig | null;
  workflow_config?: WorkflowScheduleConfig | null;
  created_by: string;
  created_at: string;
  updated_at?: string | null;
  latest_run?: ScheduleRunSummary | null;
}

/** 列表响应 */
export interface UnifiedScheduleListResponse {
  schedules: UnifiedScheduleResponse[];
}

/** 删除/触发等简单操作响应 */
export interface UnifiedScheduleActionResponse {
  message: string;
  schedule_id?: string;
}

/** 执行历史中的单次执行记录 */
export interface ScheduleRecentAction {
  scheduled_at: string | null;
  started_at: string | null;
}

/** 执行历史响应（来自 Temporal） */
export interface UnifiedScheduleExecutionsResponse {
  schedule_id: string;
  recent_actions: ScheduleRecentAction[];
  next_action_times: string[];
  error?: string;
}

// ============================================================================
// Query Parameters
// ============================================================================

export interface UnifiedScheduleListParams {
  schedule_type?: UnifiedScheduleType;
}

// ============================================================================
// Constants
// ============================================================================

export const UNIFIED_SCHEDULE_TYPE_LABELS: Record<UnifiedScheduleType, string> = {
  system_task: '系统任务',
  workflow: '工作流',
};

// ============================================================================
// Schedule Run Types
// ============================================================================

/** Run-level status */
export type ScheduleRunStatus = 'pending' | 'running' | 'completed' | 'failed' | 'partial' | 'cancelled' | 'retrying';

/** User-level status within a run */
export type ScheduleRunUserStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'skipped';

/** Summary of a single run trigger (used in list + inline on schedule card) */
export interface ScheduleRunSummary {
  run_id: string;
  fired_at: string;
  started_at: string | null;
  status: ScheduleRunStatus;
  total_users: number;
  completed_users: number;
  failed_users: number;
  duration_seconds: number | null;
  temporal_workflow_id: string | null;
  parent_run_id: string | null;
  error: string | null;
  execution_id?: string | null;
  workflow_id?: string | null;
}

/** Per-user status within a run */
export interface ScheduleRunUserResult {
  user_id: string;
  status: ScheduleRunUserStatus;
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
  error: string | null;
}

/** Full run detail with per-user breakdown */
export interface ScheduleRunDetail extends ScheduleRunSummary {
  users: ScheduleRunUserResult[];
}

/** Paginated list response */
export interface ScheduleRunListResponse {
  runs: ScheduleRunSummary[];
  total: number;
}

/** Retry response */
export interface RetryFailedRunResponse {
  message: string;
  run_id: string;
  retryable_users: number;
}
